import { ApiResult } from 'interfaces/APITypes';
import { CreateSetPayload } from 'interfaces/SetTypes';
import {
  ContentSet,
  ContentSetParams,
  GameResource,
  StudioFile,
  StudioUnit,
  UpdateSetPayload,
} from 'interfaces/StudioTypes';
import { Unit } from 'interfaces/Unit';

import { contentInstance, makeRequest } from './client';

export const getSets = async (values?: ContentSetParams): Promise<ApiResult> => {
  return makeRequest(
    contentInstance.get('/content-sets', {
      params: values,
    }),
  );
};

export const getSetSections = async (): Promise<ApiResult> => {
  return makeRequest(contentInstance.get('/content-sets/gallery'));
};

export const attachPremiumSet = async (id: string, units: Unit[]): Promise<ApiResult> => {
  const payload = {
    units: units.map((unit) => unit.serial_number),
  };
  return makeRequest(contentInstance.post(`/content-sets/${id}/attach-units`, payload));
};

export const detachPremiumSet = async (id: string, units: StudioUnit[]): Promise<ApiResult> => {
  const payload = {
    units: units.map((unit) => unit.serial_number),
  };
  return makeRequest(contentInstance.post(`/content-sets/${id}/detach-units`, payload));
};

export const getSetDetail = async (id: string, settings?: { translations?: number }): Promise<ApiResult> => {
  return makeRequest(contentInstance.get(`/content-sets/${id}`, { params: settings }));
};

export const getSyncedUnitsForPack = async (id: string): Promise<ApiResult> => {
  return makeRequest(contentInstance.get(`/content-sets/${id}/units`));
};

export const setSyncedUnitsForPack = async (id: string, units: StudioUnit[]): Promise<ApiResult> => {
  const payload = {
    units: units.map((u) => u.configuration_key),
  };
  return makeRequest(contentInstance.post(`/content-sets/${id}/sync-units`, payload));
};

export const createSet = async ({ game, images, ...values }: CreateSetPayload): Promise<ApiResult> => {
  const payload = {
    ...values,
    game: game.code,
    images: images.map((image: any) => {
      return {
        id: image.id,
        crop: image.crop,
      };
    }),
  };

  return makeRequest(contentInstance.post('/content-sets', payload));
};

export const updateSet = async (set: UpdateSetPayload, images?: StudioFile[]): Promise<ApiResult> => {
  const payload = {
    name: set.name,
    ...(set.translations ? { translations: set.translations } : {}),
    ...(set.featuredImage ? { featuredImage: set.featuredImage } : {}),
    ...(set.games ? { games: set.games } : {}),
    ...(set.tags ? { tags: set.tags } : {}),
    ...(images ? { images } : {}),
    ...(set.public !== undefined ? { public: set.public } : {}),
    ...(set.level !== undefined ? { level: set.level } : {}),
  };

  return makeRequest(contentInstance.patch(`/content-sets/${set.id}`, payload));
};

export const getGamesPerUnit = async (id: string, serial: string): Promise<ApiResult> => {
  return makeRequest(contentInstance.get(`/content-sets/${id}/units/${serial}`));
};

export const setGamesPerUnit = async (
  id: string,
  serial: string,
  games: string[],
): Promise<ApiResult<{ games: GameResource[] }>> => {
  return makeRequest(contentInstance.post(`/content-sets/${id}/units/${serial}`, { games }));
};

export const deleteSet = async (set: ContentSet): Promise<ApiResult> => {
  return makeRequest(contentInstance.delete(`/content-sets/${set.id}`));
};

export const removeImage = async (set: ContentSet, file: StudioFile): Promise<ApiResult> => {
  return makeRequest(contentInstance.delete(`/content-sets/${set.id}/images/${file.id}`));
};
